import { useCallback } from 'react';
import { contactsAPI } from '../services/api';

/**
 * Унифицированный хук для обновления контактов
 * Обеспечивает согласованность данных между всеми компонентами
 */
export const useContactUpdate = () => {
    
    /**
     * Нормализует данные контакта перед отправкой на сервер
     * @param {Object} contactData - исходные данные контакта
     * @param {Object} updates - обновления для применения
     * @returns {Object} нормализованные данные
     */
    const normalizeContactData = useCallback((contactData, updates = {}) => {
        console.log('🔧 normalizeContactData INPUT:', {
            notes: contactData.notes,
            isBrain: contactData.isBrain,
            isConnector: contactData.isConnector
        });
        
        const normalized = { ...contactData, ...updates };
        
        // Обработка поля name/fullName
        if (normalized.fullName && !normalized.name) {
            normalized.name = normalized.fullName.trim();
        } else if (normalized.name && !normalized.fullName) {
            normalized.fullName = normalized.name;
        }
        
        // Убедимся что теги в нижнем регистре
        if (normalized.tags) {
            normalized.tags = normalized.tags.map(tag => 
                typeof tag === 'string' ? tag.trim().toLowerCase() : String(tag).trim().toLowerCase()
            );
        }
        
        // Обработка relations - фильтруем невалидные
        if (normalized.relations) {
            normalized.relations = normalized.relations.filter(relation => 
                relation && 
                relation.contactId && 
                relation.contactId !== 'undefined' &&
                relation.contactName && 
                relation.type
            );
        }
        
        // Удаляем временные UI поля и проблемные поля
        const fieldsToRemove = [
            'countryData', 
            'cityData', 
            'originalName', 
            'fullName',
            'countryFlag',  // Флаг страны может вызывать проблемы
            'countryPhoneCode'  // Код телефона страны тоже
        ];
        fieldsToRemove.forEach(field => {
            if (normalized[field] !== undefined) {
                delete normalized[field];
            }
        });
        
        // Очищаем только действительно проблемные значения
        Object.keys(normalized).forEach(key => {
            if (normalized[key] === null || normalized[key] === undefined) {
                // Сохраняем важные поля даже если они null/undefined
                const importantFields = ['coordinates', 'notes', 'address', 'structuredCountry', 'structuredCity', 'isBrain', 'isConnector'];
                if (!importantFields.includes(key)) {
                    delete normalized[key];
                } else {
                    // Для важных полей преобразуем null/undefined в соответствующие значения по умолчанию
                    if (key === 'notes' && (normalized[key] === null || normalized[key] === undefined)) {
                        normalized[key] = '';
                    }
                    if ((key === 'isBrain' || key === 'isConnector') && (normalized[key] === null || normalized[key] === undefined)) {
                        normalized[key] = false;
                    }
                }
            }
        });
        
        console.log('🔧 normalizeContactData OUTPUT:', {
            notes: normalized.notes,
            isBrain: normalized.isBrain,
            isConnector: normalized.isConnector
        });
        
        return normalized;
    }, []);
    
    /**
     * Частичное обновление контакта (для одного или нескольких полей)
     * @param {string} contactId - ID контакта
     * @param {Object} updates - поля для обновления
     * @param {Object} currentContact - текущие данные контакта (опционально)
     * @returns {Promise<Object>} обновленный контакт
     */
    const updateContactPartial = useCallback(async (contactId, updates, currentContact = null) => {
        try {
            if (!contactId) {
                throw new Error('Contact ID is required for update');
            }
            
            
            // Нормализуем данные если есть текущий контакт
            const normalizedUpdates = currentContact 
                ? normalizeContactData(currentContact, updates)
                : updates;
            
            const response = await contactsAPI.editContact(contactId, normalizedUpdates);
            
            return response.data;
        } catch (error) {
            console.error('Error in partial contact update:', error);
            throw error;
        }
    }, [normalizeContactData]);
    
    /**
     * Полное обновление контакта (со всеми данными)
     * @param {string} contactId - ID контакта
     * @param {Object} contactData - полные данные контакта
     * @returns {Promise<Object>} обновленный контакт
     */
    const updateContactFull = useCallback(async (contactId, contactData) => {
        try {
            if (!contactId) {
                throw new Error('Contact ID is required for update');
            }
            
            
            // Полная нормализация данных
            const normalizedData = normalizeContactData(contactData);
            
            // Exclude system fields that should not be sent in update
            const { __v, interactions, createdAt, updatedAt, userId, ...dataToSend } = normalizedData;
            
            // Убедимся что ID присутствует
            if (!dataToSend._id) {
                dataToSend._id = contactId;
            }
            
            const response = await contactsAPI.editContact(contactId, dataToSend);
            
            return response.data;
        } catch (error) {
            console.error('Error in full contact update:', error);
            throw error;
        }
    }, [normalizeContactData]);
    
    /**
     * Умное обновление - определяет тип обновления автоматически
     * @param {string} contactId - ID контакта
     * @param {Object} data - данные для обновления
     * @param {boolean} isPartial - принудительно частичное обновление
     * @returns {Promise<Object>} обновленный контакт
     */
    const updateContact = useCallback(async (contactId, data, isPartial = false) => {
        try {
            // Определяем тип обновления
            const hasMainFields = data.name || data.fullName || data.email || data.phone;
            const hasOnlySpecificFields = Object.keys(data).length <= 3 && 
                (data.notes !== undefined || data.structuredCountry !== undefined || 
                 data.tags !== undefined || data.importantDates !== undefined);
            
            if (isPartial || hasOnlySpecificFields) {
                return await updateContactPartial(contactId, data);
            } else {
                return await updateContactFull(contactId, data);
            }
        } catch (error) {
            console.error('Error in smart contact update:', error);
            throw error;
        }
    }, [updateContactPartial, updateContactFull]);
    
    return {
        updateContact,
        updateContactPartial,
        updateContactFull,
        normalizeContactData
    };
};

export default useContactUpdate; 
